import { Component, Vue, Prop } from 'vue-property-decorator';
import { AxiosError } from 'axios';
import { validationRules } from '@/support/ValidationRules';
import { User as UserModel } from '@/models/User';
import { Contact } from '@/models/Contact';

import ErrorHandler from '@/support/ErrorHandler';
import LoaderCard from '@/components/loader-card/LoaderCard';
import MiPassword, { MiPasswordVisibility } from '@/components/mi-password/MiPassword';
import { addClass, removeClass } from '@/support/Html';

@Component<CreateDialog>({
  filters: {
    capitalize(text: string) {
      if (!text) {
        return;
      }

      return text.charAt(0).toUpperCase() + text.slice(1);
    },
  },
  components: {
    LoaderCard,
    MiPassword,
  }
})
export default class CreateDialog extends Vue {
  @Prop()
  protected role?: string;

  protected user: UserModel = new UserModel();
  protected createdUser: UserModel | null = null;
  protected isLoading: boolean = false;
  protected activeStepper: boolean = true;
  protected currentStep: number = 1;
  protected valid: boolean = true;
  protected rules: object = validationRules(this.getTranslation);
  protected chosenTypeName: string | undefined = '';


  protected roleMap: object = {
    Admin: 'administrator',
    Stylists: 'stylist',
    Contacts: 'contact',
    Support: 'support',
  }

  protected isPasswordValid: boolean = false;
  protected visibility: MiPasswordVisibility = {
    ruleList: true,
    showPassword: true,
  }

  public getTranslation(key: string) {
    return this.$t(key);
  }

  public mounted() {
    const pageHtml = document.querySelectorAll('html')[0];
    addClass(pageHtml, 'overflow-y-hidden');
  }

  protected close() {
    this.$emit('input', false);
    this.currentStep = 1;
    this.clear();
    const pageHtml = document.querySelectorAll('html')[0];
    removeClass(pageHtml, 'overflow-y-hidden');
  }

  protected reset() {
    this.clear();
    this.resetMiPassword();
    this.currentStep = 1;
  }

  private resetMiPassword() {
    const miPassword: MiPassword = this.$refs.miPassword as MiPassword;
    if(miPassword) {
      miPassword.reset();
      miPassword.resetRules();
    }
  }

  protected clear() {
    this.user = new UserModel();
    if(this.$refs.createUserForm) {
      (this.$refs.createUserForm as HTMLFormElement).reset();
    }
  }

  protected canSubmit(password: string, confirmation: string) {
    if(!password || !confirmation) {
      return false;
    }
    return password === confirmation;
  }

  protected create() {
    this.isLoading = true;

    if (this.user.first_name) {
      this.user.first_name = this.capitalize(this.user.first_name);
    }

    if (this.user.last_name) {
      this.user.last_name = this.capitalize(this.user.last_name);
    }

    if (this.role) {
      this.user.role = (this.roleMap as any)[this.role];
      if (this.role === 'Contacts') {
         (this.user as Contact).profile = {};
      }
    }

    new UserModel()
    .create(this.user)
    .then((user: UserModel) => {
      this.isLoading = false;
      this.$emit('userCreated', user);
      this.currentStep = 3;
      this.activeStepper = false;
      this.createdUser = user;
    })
    .catch((error: AxiosError) => {
      ErrorHandler.network(error);
      this.isLoading = false;
      this.reset();
    });
  }

  protected get showSimpleDialog() {
    return this.role === 'Contacts';
  }

  protected goToStep(step: number) {
    if (this.currentStep < step || !this.activeStepper) {
      return;
    }

    this.currentStep = step;
  }

  protected validateForm() {
    if (this.showSimpleDialog) {
      this.currentStep = 2;
      return;
    }

    if (!(this.$refs.createUserForm as HTMLFormElement).validate() || !this.valid) {
      return;
    }

    const miPassword: MiPassword = this.$refs.miPassword as MiPassword;

    if(!miPassword) {
      return;
    }

    const password = miPassword.getPassword().password;
    const confirmation = miPassword.getPassword().confirmation;

    if (!this.canSubmit(password, confirmation)) {
      return;
    }

    this.user.password = password;
    this.currentStep = 2;
  }

  protected capitalize(text: string) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  protected activateMfa() {
    if (!this.createdUser) {
      return;
    }

    this.$router.push(`/users/${this.createdUser.id}?isActiveMfa=false`);
  }
}
